import {
    Component,
    ViewChildren,
    QueryList,
    ChangeDetectionStrategy,
} from "@angular/core";
import { first, map, Observable } from "rxjs";
import { Store } from "@ngrx/store";

import { SimpleToggleableComponent } from "../components/simpleToggleable.component";
import {
    productDefinitions,
    getInitialCategory,
    sonstige,
} from "../data/product.constants";
import {
    SectionDefinition,
    DefinitionIdentifier,
    ProductDefinition,
    isSectionDefinition,
} from "../data/product";
import {
    Protocol,
    Attachment,
    ConsultationProcess,
    Offer,
} from "../data/model";
import { UtilsComponent } from "../components/utils.component";
import {
    Demand,
    Recommendation,
    Decision,
    Division,
} from "../services/consultation.service";
import { AnalysisValidity } from "../store/validity/state";
import { PersistenceService } from "../store/model/persistence.service";
import { RootState } from "../store/root";
import { stateBuilder } from "../store/utils";
import { trackEntity } from "../utils/utils";

@Component({
    selector: "zbd-analysis",
    templateUrl: "analysis.component.html",
    styles: [
        `
            .content-separator {
                margin: 20px 20px 30px 20px;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisComponent extends UtilsComponent {
    @ViewChildren("toggleable")
    toggleables: QueryList<SimpleToggleableComponent>;

    state$: Observable<LocalState>;
    validity$: Observable<AnalysisValidity>;
    manualOffer$: Observable<Offer>;

    productDefinitions = productDefinitions;

    categories: SectionDefinition[];
    lineContexts: Record<
        string,
        { category: DefinitionIdentifier; line: DefinitionIdentifier }
    > = {};

    selectedCategory: SectionDefinition;
    selectedLine: DefinitionIdentifier;
    view: ViewState;

    trackEntity = trackEntity;

    constructor(
        public persistence: PersistenceService,
        store: Store<RootState>,
    ) {
        super();
        this.state$ = LocalState.create(store);
        this.validity$ = store.select<AnalysisValidity>(
            (s) => s.validity.analysis,
        );
        this.manualOffer$ = store
            .select((s) => s.model.ConsultationProcess)
            .pipe(
                map((cp) =>
                    cp.Application === null
                        ? cp.Protocol.Offers[0]
                        : cp.Protocol.Offers.find(
                              (offer: Offer) => offer.ExternalId === null,
                          ),
                ),
            );

        this.categories = productDefinitions.sections as SectionDefinition[];

        this.categories.forEach((category) => {
            this.collectCategoryLines(category).forEach(
                (line: DefinitionIdentifier) => {
                    this.lineContexts[line.key] = {
                        category: category.name,
                        line: line,
                    };
                },
            );
        });

        this.register(
            this.state$.pipe(first()).subscribe((state) => {
                this.selectCategory(
                    getInitialCategory(state.cp.Application),
                    state.protocol,
                );
            }),
        );
    }

    createViewState(state: LocalState): ViewState {
        const view: ViewState = {
            lines: [],
            divisions: [],
            divisionDemands: [],
        };

        if (!this.categories.includes(this.selectedCategory)) {
            this.selectedCategory = this.categories[0];
        }

        const divisions = state.protocol.Offers.map(
            (offer) => offer.Divisions,
        ).reduce((allOffers, offers) => allOffers.concat(offers));

        // filter lines by selected category
        divisions.forEach((div) => {
            let lineContext = this.lineContexts[div.GroupKey];
            if (!lineContext) {
                // unknown line, try to map it to the correct category and add it to the known lines
                const category = this.categories.find(
                    (category) => category.name.key === div.CategoryKey,
                );
                lineContext = {
                    category: (category && category.name) || sonstige,
                    line: {
                        key: div.GroupKey,
                        displayName: div.GroupName,
                        printName: div.GroupName,
                    },
                };
                this.lineContexts[lineContext.line.key] = lineContext;
            }
            if (
                lineContext.category.key === this.selectedCategory.name.key &&
                !view.lines.includes(lineContext.line)
            ) {
                view.lines.push(lineContext.line);
            }
        });

        if (!view.lines.includes(this.selectedLine)) {
            this.selectedLine = view.lines[0];
        }
        if (this.selectedLine) {
            const seenProducts = {};
            divisions.forEach((div) => {
                if (div.GroupKey === this.selectedLine.key) {
                    view.divisions.push(div);
                    if (!seenProducts[div.ProductKey]) {
                        seenProducts[div.ProductKey] = true;
                        view.divisionDemands.push(div);
                    }
                }
            });
        }

        this.view = view;
        return view;
    }

    collectCategoryLines(
        def: SectionDefinition | ProductDefinition,
        list: DefinitionIdentifier[] = [],
    ) {
        if (isSectionDefinition(def)) {
            def.sections.forEach((subsec) => {
                this.collectCategoryLines(subsec, list);
            });
        } else {
            if (!list.includes(def.line)) {
                list.push(def.line);
            }
        }
        return list;
    }

    selectCategory(category: SectionDefinition, protocol: Protocol): void {
        this.selectedCategory = category;
    }

    selectLine(line: DefinitionIdentifier, protocol: Protocol): void {
        this.selectedLine = line;
    }

    trackByLine(index: number, item: DefinitionIdentifier) {
        return item.key;
    }

    getAcceptAllState(protocol: Protocol): boolean {
        let allSet = true;
        protocol.Offers.forEach((offer) => {
            offer.Divisions.forEach((division) => {
                allSet =
                    allSet &&
                    division.Decision.IsAccepted &&
                    division.Decision.IsDecisionSameAsRecommendation;
            });
        });
        return allSet;
    }

    acceptAll(protocol: Protocol, event): void {
        const checked = event;
        protocol.Offers.forEach((offer) => {
            offer.Divisions.forEach((division) => {
                this.persistence.updateDecision(
                    division.Decision,
                    "IsDecisionSameAsRecommendation",
                    checked,
                );
                this.persistence.updateDecision(
                    division.Decision,
                    "IsAccepted",
                    checked ? true : null,
                );
            });
        });
    }

    addAttachment(protocol: Protocol) {
        return (attachment: Attachment, content: File): void => {
            this.persistence.addAttachment(
                "Protocols",
                protocol,
                "Attachments",
                attachment,
                content,
            );
        };
    }

    onTabSelected(displayName: string, protocol: Protocol) {
        for (const line of this.view.lines) {
            if (line.displayName === displayName) {
                this.selectLine(line, protocol);
                break;
            }
        }
    }

    onToggleableOpened(item: SimpleToggleableComponent) {
        this.toggleables
            .toArray()
            .filter((accordion) => accordion !== item && accordion.isOpen)
            .forEach((element) => element.close());
    }
}

class ViewState {
    lines: DefinitionIdentifier[];
    divisions: Division[];
    divisionDemands: Division[];
}

class LocalState {
    cp: ConsultationProcess;
    protocol: Protocol;

    static create(store: Store<RootState>): Observable<LocalState> {
        return stateBuilder(store, new LocalState())
            .apply("cp", (s) => s.model.ConsultationProcess)
            .apply("protocol", (s) => s.model.ConsultationProcess.Protocol)
            .state$;
    }
}
